import { OutlineElement } from '@phase2/outline-core';
import { html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';
import { debounce } from '../../../utilities/debounce';

import globalStyles from './pc-wysiwyg.lightDom.css.lit';

/**
 * Wysiwyg component
 * @element pc-wysiwyg
 * @slot - This element has a default slot
 */
@customElement('pc-wysiwyg')
export class PCWysiwyg extends OutlineElement {
  adoptedStylesheets: AdoptedStyleSheets;

  @state()
  expanded = false;

  @property({ type: Boolean, attribute: 'with-gradient' })
  withGradient = true;

  @property({ type: Boolean, attribute: 'is-collapsable' })
  isCollapsable = false;

  @property({ attribute: 'collapsed-label' })
  collapsedLabel = 'Show More';

  @property({ attribute: 'expanded-label' })
  expandedLabel = 'Show Less';

  collapsablePanel: HTMLElement;

  triggerButton: HTMLButtonElement;

  height = 0;

  connectedCallback() {
    super.connectedCallback();
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);

    if (this.isCollapsable) {
      window.addEventListener('resize', debounce(this._handleResize, 50));
    }
  }

  disconnectedCallback() {
    if (this.isCollapsable) {
      window.removeEventListener('resize', debounce(this._handleResize, 50));
    }
  }

  render(): TemplateResult {
    return html` <slot></slot> `;
  }

  protected firstUpdated(): void {
    if (this.isCollapsable) {
      const trigger = this.querySelector(
        '.dropdown-trigger'
      ) as HTMLButtonElement;
      const panel = this.querySelector('.collapsable-panel') as HTMLElement;

      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.addEventListener('click', () => this._toggleExpandedState());
        this.triggerButton = trigger;
      }

      if (panel) {
        this.collapsablePanel = panel;
      }
    }
  }

  protected updated(): void {
    if (this.isCollapsable) {
      this.triggerButton.setAttribute('aria-expanded', `${this.expanded}`);
      const buttonLabel = this.triggerButton.querySelector('span');
      const buttonIcon = this.triggerButton.querySelector('pc-icon');

      if (buttonLabel) {
        buttonLabel.textContent = this.expanded
          ? this.expandedLabel
          : this.collapsedLabel;
      }

      if (buttonIcon) {
        buttonIcon.name = this.expanded ? 'minus' : 'plus';
      }

      this.collapsablePanel.setAttribute('aria-hidden', `${!this.expanded}`);
    }
  }

  _calcContentHeight() {
    if (this.collapsablePanel) {
      this.collapsablePanel.removeAttribute('aria-hidden');
      const rect = this.collapsablePanel.getBoundingClientRect();
      const height = Math.round(rect.height);

      if (this.height !== height) {
        this.style.setProperty('--wysiwyg-expanded-height', `${height}px`);
        this.height = height;
      }

      this.collapsablePanel.setAttribute('aria-hidden', `${!this.expanded}`);
    }
  }

  _handleResize = () => {
    if (this.expanded) {
      this._calcContentHeight();
    }
  };

  _toggleExpandedState() {
    if (!this.expanded) {
      this._calcContentHeight();
    }

    setTimeout(() => {
      this.expanded = !this.expanded;

      const options = {
        detail: { expanded: this.expanded },
        bubbles: true,
        composed: true,
      };

      this.dispatchEvent(new CustomEvent('contentChange', options));
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-wysiwyg': PCWysiwyg;
  }
}
